import React from "react";
import { GistType } from "../../services/types";
import DOMPurify from "dompurify";
import { ActivityMeta } from "./DiscussionMap";
import DiscussionCommentsNavigator from "./DiscussionCommentsNavigator";

interface DiscussionMapPreviewProps {
  gist: GistType | null;
  collapsed: boolean;
  onCollapse: () => void;
  onExpand: () => void;
  onOpenFullGist?: () => void;
  activityMeta?: ActivityMeta;
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

const relativeTime = (dateStr: string): string => {
  const diff = Date.now() - Date.parse(dateStr);
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return dateFormatter.format(Date.parse(dateStr));
};

const stripHtml = (html: string): string => {
  const cleaned = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  return cleaned.replace(/\s+/g, " ").trim();
};

const PlaceholderButton: React.FC<{
  label: string;
  icon: string;
  variant?: "primary" | "secondary";
}> = ({ label, icon, variant = "secondary" }) => (
  <button
    type="button"
    className={`discussion-map-preview-btn discussion-map-preview-btn-${variant}`}
    disabled
    title="Coming soon"
  >
    <span>{icon}</span>
    {label}
  </button>
);

const DiscussionMapPreview: React.FC<DiscussionMapPreviewProps> = ({ 
  gist,
  collapsed,
  onCollapse,
  onExpand,
  onOpenFullGist,
  activityMeta,
}) => {
  if (collapsed) {
    return (
      <button
        type="button"
        className="discussion-map-preview-handle"
        onClick={onExpand}
        aria-label="Expand preview"
      >
        <span className="discussion-map-preview-handle-icon">›</span>
        <span className="discussion-map-preview-handle-label">
          {gist ? "SELECTED GIST" : "PREVIEW"}
        </span>
      </button>
    );
  }

  const renderContent = () => {
    if (!gist) {
      return (
        <div className="discussion-map-preview-empty">
          <div className="discussion-map-preview-empty-graphic">
            <span className="discussion-map-preview-empty-tree-icon">🌿</span>
          </div>
          <p className="discussion-map-preview-empty-headline">
            Select a GIST to preview it
          </p>
          <p className="discussion-map-preview-empty-sub">
            Click any node in the tree on the left. You'll see its title, summary,
            body excerpt, version count, comment count, and direct replies here.
          </p>
          <div className="discussion-map-preview-empty-features">
            <span>📝 Title &amp; Summary</span>
            <span>🔢 Version count</span>
            <span>💬 Comment count</span>
            <span>↩ Reply structure</span>
          </div>
        </div>
      );
    }

    const latestVersion = gist.versions?.[0];
    const latestEdit = latestVersion?.edits?.[0];
    const point = latestVersion?.point;
    const rawBody = latestEdit?.body ?? "";
    const bodyPreview = rawBody ? stripHtml(rawBody).slice(0, 300) : null;
    const bodyIsTruncated = rawBody ? stripHtml(rawBody).length > 300 : false;
    const versionCount = gist.versions?.length ?? 0;
    const commentCount = gist.versions?.reduce((acc, v) => acc + (v.edits?.reduce((eacc, e) => eacc + (e.comments?.length ?? 0), 0) ?? 0), 0) ?? 0;
    const createdAt = gist.createdAt ? relativeTime(gist.createdAt) : null;
    const authorName = latestVersion?.user?.name;

    return (
      <div className="discussion-map-preview-card">
        <div className="discussion-map-preview-header">
          <div className="discussion-map-preview-chip">GIST</div>
          <div className="discussion-map-preview-meta-row">
            {authorName && <span className="discussion-map-preview-author">{authorName}</span>}
            {createdAt && <span className="discussion-map-preview-date">{createdAt}</span>}
          </div>
        </div>

        <h2 className="discussion-map-preview-title">{gist.title}</h2>

        {point && (
          <div className="discussion-map-preview-point-zone">
            <p className="discussion-map-preview-point">{point}</p>
          </div>
        )}

        {bodyPreview && (
          <div className="discussion-map-preview-body">
            <p>
              {bodyPreview}
              {bodyIsTruncated && <span className="discussion-map-preview-ellipsis"> …</span>}
            </p>
          </div>
        )}

        <div className="discussion-map-preview-stats">
          <span className="discussion-map-preview-stat">
            <span className="discussion-map-preview-stat-value">{versionCount}</span>
            <span className="discussion-map-preview-stat-label">{versionCount === 1 ? "version" : "versions"}</span>
          </span>
          <span className="discussion-map-preview-stat-divider">·</span>
          <span className="discussion-map-preview-stat">
            <span className="discussion-map-preview-stat-value">{commentCount}</span>
            <span className="discussion-map-preview-stat-label">{commentCount === 1 ? "comment" : "comments"}</span>
          </span>
          {Array.isArray(gist.gists) && gist.gists.length > 0 && (
            <>
              <span className="discussion-map-preview-stat-divider">·</span>
              <span className="discussion-map-preview-stat">
                <span className="discussion-map-preview-stat-value">{gist.gists.length}</span>
                <span className="discussion-map-preview-stat-label">{gist.gists.length === 1 ? "reply" : "replies"}</span>
              </span>
            </>
          )}
        </div>

        {versionCount > 1 && (
          <div className="discussion-map-preview-version-strip">
            <span className="discussion-map-preview-version-label">Versions:</span>
            {gist.versions!.map((v, i) => (
              <span
                key={v.id}
                className={`discussion-map-preview-version-dot${i === 0 ? " is-active" : ""}`}
                title={v.point || `Version ${i + 1}`}
              >
                {i + 1}
              </span>
            ))}
          </div>
        )}

        {activityMeta && activityMeta.lastActivityAt && (
          <div className="discussion-map-preview-activity-zone">
            <h3 className="discussion-map-preview-activity-title">Activity</h3>
            <div className="discussion-map-preview-activity-row">
              <span className="discussion-map-preview-activity-label">Last activity:</span>
              <span className="discussion-map-preview-activity-value">
                {relativeTime(new Date(activityMeta.lastActivityAt).toISOString())}
              </span>
            </div>
            {activityMeta.latestReason && (
              <div className="discussion-map-preview-activity-row">
                <span className="discussion-map-preview-activity-label">Latest change:</span>
                <span className="discussion-map-preview-activity-value">{activityMeta.latestReason}</span>
              </div>
            )}
            {activityMeta.isRecentSelfOrSubtree && (
              <div className="discussion-map-preview-activity-row" style={{ marginTop: '6px' }}>
                <span className="discussion-map-preview-badge-recent">Recent</span>
              </div>
            )}
          </div>
        )}

        <DiscussionCommentsNavigator comments={latestEdit?.comments ?? []} />

        <div className="discussion-map-preview-actions">
          <button
            type="button"
            className="discussion-map-preview-btn discussion-map-preview-btn-primary"
            disabled={!gist}
            onClick={onOpenFullGist}
            title={gist ? "Open selected GIST in Reader View" : "Select a GIST first"}
          >
            <span>↗</span>
            Open full GIST
          </button>
          <PlaceholderButton label="Reply as GIST" icon="↩" />
          <PlaceholderButton label="Compare versions" icon="⟺" />
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className="discussion-map-preview-handle"
        onClick={onCollapse}
        aria-label="Collapse preview"
        title="Collapse preview"
      >
        <span className="discussion-map-preview-handle-icon">‹</span>
        <span className="discussion-map-preview-handle-label">
          {gist ? "HIDE PREVIEW" : "COLLAPSE"}
        </span>
      </button>

      <div className="discussion-map-preview-main-content">
        {renderContent()}
      </div>
    </>
  );
};

export default DiscussionMapPreview;
